import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import DataTable, { type DataTableColumn, type RowKey } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  createCmdbCard, deleteCmdbCard, errorMessage, fetchCmdbCard, fetchCmdbCards,
  fetchCmdbOptions, updateCmdbCard,
} from '../../lib/api';
import { readOnlyNotice } from '../../lib/permissions';
import type { CmdbAttribute, CmdbCardRow, CmdbClassMeta, CmdbMetaResponse, CmdbOption } from '../../lib/types';
import CardFormModal from './CardFormModal';
import DeleteCardModal from './DeleteCardModal';
import StammIcon from './StammIcon';
import { referenceTargets } from './cardHelpers';
import {
  allVersions, filterProductGroups, groupProducts, groupsWithMatchingVersions, latestOfficialVersion,
  newProductVersionValues, productVersionLabel, type ProductGroup,
} from './productCatalogModel';
import { sbomUploadLink, sbomUploadTitle } from './sbomUpload';

type Dialog =
  | { kind: 'closed' }
  | { kind: 'form'; meta: CmdbClassMeta; row: CmdbCardRow | null; options: Record<string, CmdbOption[]>; initialValues?: Record<string, unknown>; notice?: string }
  | { kind: 'delete'; meta: CmdbClassMeta; row: CmdbCardRow };

interface Props {
  meta: CmdbMetaResponse;
  canWrite: boolean;
  canImport: boolean;
  onCountsChange: () => Promise<void>;
  renderValue: (attr: CmdbAttribute, row: CmdbCardRow) => ReactNode;
}

/** Products own their versions in the UI (DEV-111); custom versions sit under their base version. */
export default function ProductCatalog({ meta, canWrite, canImport, onCountsChange, renderValue }: Props): ReactNode {
  const productMeta = meta.classes.find((entry) => entry.name === 'ProduktType')!;
  const versionMeta = meta.classes.find((entry) => entry.name === 'ProduktVersion')!;
  const toast = useToast();
  const { t, i18n } = useLingui();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<{ products: CmdbCardRow[]; versions: CmdbCardRow[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<RowKey>>(new Set());
  const [dialog, setDialog] = useState<Dialog>({ kind: 'closed' });
  const [dialogBusy, setDialogBusy] = useState(false);
  const loadSeq = useRef(0);
  const dialogSeq = useRef(0);
  const focusedRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLElement>(null);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      // Search spans both complete lists, so a version match never loses its product.
      const [products, versions] = await Promise.all([
        fetchCmdbCards('ProduktType'), fetchCmdbCards('ProduktVersion'),
      ]);
      if (seq !== loadSeq.current) return;
      setData({ products: products.rows, versions: versions.rows });
      setError(null);
    } catch (caught) {
      if (seq === loadSeq.current) setError(errorMessage(caught));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { ++loadSeq.current; ++dialogSeq.current; };
  }, [load]);

  // Fallback product names are translated, so the groups follow the UI language.
  const groups = useMemo(() => groupProducts(data?.products ?? [], data?.versions ?? []), [data, i18n.locale]);
  const visibleGroups = useMemo(() => filterProductGroups(groups, search), [groups, search]);
  const visibleVersions = useMemo(
    () => visibleGroups.reduce((sum, group) => sum + group.versionCount, 0), [visibleGroups]);
  const focusId = Number(params.get('id')) || null;
  const focusClass = params.get('klasse');
  const focusGroup = focusId === null ? undefined : groups.find((group) =>
    focusClass === 'ProduktVersion'
      ? allVersions(group).some((version) => version.id === focusId)
      : group.product?.id === focusId);
  const focusKey = focusGroup?.key;
  const focusToken = focusId === null ? null : `${focusClass}:${focusId}`;

  useEffect(() => {
    if (!focusKey || !focusToken) { focusedRef.current = null; return; }
    if (focusedRef.current === focusToken) return;
    focusedRef.current = focusToken;
    setSearch('');
    setExpanded((current) => new Set([...current, focusKey]));
    // Allow the table's pagination reveal and expanded content to commit first.
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = containerRef.current?.querySelector('.component-version.is-focused')
          ?? containerRef.current?.querySelector('.stamm-focus');
        target?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusKey, focusToken]);

  const clearFocus = (): void => {
    focusedRef.current = null;
    if (!params.has('id')) return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('id');
      return next;
    }, { replace: true });
  };

  const changeSearch = (value: string): void => {
    clearFocus();
    setSearch(value);
    // A version match opens its product, so the hit is visible right away.
    setExpanded(new Set(groupsWithMatchingVersions(groups, value)));
  };

  const openForm = async (targetMeta: CmdbClassMeta, row: CmdbCardRow | null, group?: ProductGroup): Promise<void> => {
    if (!canWrite) return;
    const seq = ++dialogSeq.current;
    setDialogBusy(true);
    try {
      const targets = referenceTargets(targetMeta);
      const [options, fresh] = await Promise.all([
        targets.length ? fetchCmdbOptions(targets) : Promise.resolve({ options: {} }),
        row ? fetchCmdbCard(targetMeta.name, row.id) : Promise.resolve(null),
      ]);
      if (seq !== dialogSeq.current) return;
      const latest = group ? latestOfficialVersion(group) : null;
      const latestLabel = latest ? productVersionLabel(latest) : '';
      setDialog({ kind: 'form', meta: targetMeta, row: fresh, options: options.options,
        initialValues: group?.product ? newProductVersionValues(group) : undefined,
        notice: group && latest ? t`Vorgängerversion ${latestLabel} vorbelegt.` : undefined,
      });
    } catch (caught) {
      if (seq === dialogSeq.current) toast.error(errorMessage(caught));
    } finally {
      if (seq === dialogSeq.current) setDialogBusy(false);
    }
  };

  const openDelete = async (targetMeta: CmdbClassMeta, row: CmdbCardRow): Promise<void> => {
    if (!canWrite) return;
    const seq = ++dialogSeq.current;
    setDialogBusy(true);
    try {
      const fresh = await fetchCmdbCard(targetMeta.name, row.id);
      if (seq === dialogSeq.current) setDialog({ kind: 'delete', meta: targetMeta, row: fresh });
    } catch (caught) {
      if (seq === dialogSeq.current) toast.error(errorMessage(caught));
    } finally {
      if (seq === dialogSeq.current) setDialogBusy(false);
    }
  };

  const afterWrite = async (message: string): Promise<void> => {
    setDialog({ kind: 'closed' });
    toast.success(message);
    await load();
    await onCountsChange();
  };

  const uploadAction = (targetMeta: CmdbClassMeta, row: CmdbCardRow): ReactNode => {
    const link = canImport ? sbomUploadLink(targetMeta.name, row) : null;
    const { label } = row;
    return link === null ? null : (
      <Link className="stamm-action-button" to={link} aria-label={t`SBOM für ${label} hochladen`}
        title={sbomUploadTitle(targetMeta.name, row)} onClick={(event) => event.stopPropagation()}>
        <Icon name="upload" size={18} />
      </Link>
    );
  };

  const bomAction = (targetMeta: CmdbClassMeta, row: CmdbCardRow): ReactNode => {
    if (targetMeta.name !== 'ProduktVersion') return null;
    const { label } = row;
    // The Stückliste of this version in the overview (DEV-114).
    return <Link className="stamm-action-button" to={`/stammdaten?klasse=ProduktVersionKomponente&version=${row.id}`}
      aria-label={t`Stückliste von ${label} öffnen`} title={t`Stückliste`} onClick={(event) => event.stopPropagation()}>
      <StammIcon name="link" />
    </Link>;
  };

  const actions = (targetMeta: CmdbClassMeta, row: CmdbCardRow): ReactNode => {
    const { label } = row;
    return <div className="stamm-actions">
      {bomAction(targetMeta, row)}
      {uploadAction(targetMeta, row)}
      <button type="button" className="stamm-action-button" disabled={!canWrite || dialogBusy}
        aria-label={t`${label} bearbeiten`} title={canWrite ? t`Bearbeiten` : readOnlyNotice('cmdb.write')}
        onClick={() => void openForm(targetMeta, row)}><Icon name="edit" size={18} /></button>
      <button type="button" className="stamm-action-button stamm-delete-button" disabled={!canWrite || dialogBusy}
        aria-label={t`${label} löschen`} title={canWrite ? t`Löschen` : readOnlyNotice('cmdb.write')}
        onClick={() => void openDelete(targetMeta, row)}><Icon name="trash" size={18} /></button>
    </div>;
  };

  const addVersion = (group: ProductGroup): ReactNode => {
    if (!group.product || !versionMeta.can_create) return null;
    const { name } = group;
    return <button type="button" className="component-add-version" disabled={!canWrite || dialogBusy}
      title={canWrite ? t`Version für ${name} anlegen` : readOnlyNotice('cmdb.write')}
      aria-label={t`Version für ${name} anlegen`}
      onClick={(event) => { event.stopPropagation(); void openForm(versionMeta, null, group); }}>
      <Icon name="plus" /><Trans>Version</Trans>
    </button>;
  };

  const rowActions = (group: ProductGroup): ReactNode => (
    <div className="product-row-actions">
      {group.product ? uploadAction(productMeta, group.product) : null}
      {addVersion(group)}
    </div>
  );

  const detailFields = (targetMeta: CmdbClassMeta, row: CmdbCardRow): ReactNode => (
    <dl className="stamm-detail-grid">
      {targetMeta.attributes.map((attr) => (
        <div key={attr.name}><dt title={attr.help}>{attr.label}{attr.system ? <Chip><Trans>System</Trans></Chip> : null}</dt><dd>{renderValue(attr, row)}</dd></div>
      ))}
    </dl>
  );

  const versionArticle = (group: ProductGroup, version: CmdbCardRow, custom: boolean): ReactNode => {
    const focused = focusClass === 'ProduktVersion' && focusId === version.id;
    const instance = custom ? String(version.display.ProductInstanceID ?? '').trim() : '';
    const versionLabel = productVersionLabel(version);
    return (
      <article key={version.id} className={`component-version${custom ? ' product-custom-version' : ''}${focused ? ' is-focused' : ''}`}
        aria-label={`${group.name} ${productVersionLabel(version)}`}>
        <div className="component-version-head">
          <div className="component-version-title">
            <StammIcon name="layers" /><strong>{productVersionLabel(version)}</strong>
            {custom ? <Chip tone="info" title={instance ? t`Kundenversion von ${instance}` : undefined}><Trans>Kundenversion</Trans></Chip> : null}
          </div>
          {actions(versionMeta, version)}
        </div>
        {instance ? <p className="product-custom-instance"><Trans>Produktinstanz: {instance}</Trans></p> : null}
        <details className="component-technical" open={focused || undefined}>
          <summary><Trans>Technische Details<span className="visually-hidden"> zu Version {versionLabel}</span></Trans></summary>
          {detailFields(versionMeta, version)}
        </details>
      </article>
    );
  };

  const columns: readonly DataTableColumn<ProductGroup>[] = [
    { key: 'product', header: t`Produkt`, width: '38%', sortValue: (group) => group.name,
      render: (group) => <div className="component-identity">
        <span className="stamm-record-icon"><StammIcon name="box" /></span>
        <div className="component-name"><strong>{group.name}</strong>
          {group.modelSeries ? <span>{group.modelSeries}</span> : null}
          {!group.product ? <span className="component-unassigned"><Trans>Ohne Produktzuordnung</Trans></span> : null}
        </div>
      </div>,
    },
    { key: 'versions', header: t`Versionen`, sortValue: (group) => group.versionCount,
      render: (group) => {
        const customCount = group.versionCount - group.versions.length;
        const { name, versionCount } = group;
        return <div className="component-versions-preview">
          {group.versions.slice(0, 3).map(({ version }) => {
            const versionLabel = productVersionLabel(version);
            return <button key={version.id} type="button" className="component-version-link"
              aria-label={t`Version ${versionLabel} von ${name} öffnen`}
              onClick={(event) => { event.stopPropagation(); setParams({ klasse: 'ProduktVersion', id: String(version.id) }); }}>
              <Chip mono>{versionLabel}</Chip>
            </button>;
          })}
          {group.versionCount === 0 ? <span className="component-no-versions"><Trans>Noch keine Version</Trans></span>
            : <span className="product-version-count">
              {customCount > 0
                ? t`${plural(versionCount, { one: '# Version', other: '# Versionen' })}, davon ${plural(customCount, { one: '# Kundenversion', other: '# Kundenversionen' })}`
                : <Plural value={versionCount} one="# Version" other="# Versionen" />}
            </span>}
        </div>;
      },
    },
    { key: 'actions', header: <span className="visually-hidden"><Trans>Aktionen</Trans></span>, mobileLabel: t`Aktionen`, width: '168px', align: 'right', render: rowActions },
  ];

  const renderDetail = (group: ProductGroup): ReactNode => (
    <div className="component-expanded">
      <div className="component-expanded-head">
        <strong><Plural value={group.versionCount} one="# Version" other="# Versionen" /></strong>
        {group.product ? <div className="component-detail-actions">{actions(productMeta, group.product)}</div> : null}
      </div>
      {group.versionCount ? <div className="component-version-list">
        {group.versions.map((node) => {
          const baseLabel = productVersionLabel(node.version);
          return <div key={node.version.id} className="product-version-node">
            {versionArticle(group, node.version, false)}
            {node.customVersions.length ? <div className="product-custom-versions" aria-label={t`Kundenversionen auf Basis von ${baseLabel}`}>
              {node.customVersions.map((version) => versionArticle(group, version, true))}
            </div> : null}
          </div>;
        })}
        {group.unattachedCustomVersions.length ? <div className="product-version-node">
          <p className="product-unattached-note"><Trans>Kundenversionen ohne Basisversion dieses Produkts</Trans></p>
          <div className="product-custom-versions">
            {group.unattachedCustomVersions.map((version) => versionArticle(group, version, true))}
          </div>
        </div> : null}
      </div> : <EmptyState compact title={t`Noch keine Version erfasst`} action={addVersion(group)}><Trans>Legen Sie die erste Version an oder laden Sie eine SBOM hoch.</Trans></EmptyState>}
      {group.product ? <details className="component-technical component-master-data"><summary><Trans>Produkt-Details</Trans></summary>{detailFields(productMeta, group.product)}</details> : null}
    </div>
  );

  return (
    <section ref={containerRef} className="card stamm-content component-catalog product-catalog" aria-labelledby="product-catalog-title" aria-busy={loading}>
      <div className="card-head table-head stamm-content-head">
        <div className="stamm-title-row"><h2 id="product-catalog-title">{productMeta.label_plural}</h2></div>
        {productMeta.can_create ? <button type="button" className="primary stamm-create" disabled={!canWrite || dialogBusy}
          title={canWrite ? undefined : readOnlyNotice('cmdb.write')} onClick={() => void openForm(productMeta, null)}><Icon name="plus" /><Trans>Produkt anlegen</Trans></button> : null}
      </div>
      <div className="table-toolbar">
        <input type="search" value={search}
          placeholder={t`Produkt, Modellreihe oder Version suchen…`} aria-label={t`Produkte und Versionen durchsuchen`}
          onChange={(event) => changeSearch(event.target.value)} />
        {data ? <div className="table-toolbar-options">
          <span className="table-count" aria-live="polite">
            <Plural value={visibleGroups.filter((group) => group.product).length} one="# Produkt" other="# Produkte" />
            {' · '}<Plural value={visibleVersions} one="# Version" other="# Versionen" />
          </span>
        </div> : null}
      </div>
      {error ? <Banner kind="error" sub={data ? t`Die angezeigten Daten konnten nicht aktualisiert werden.` : undefined}>{error}</Banner> : null}
      {loading && !data ? <PageSpinner text={t`Lädt Produkte und Versionen…`} /> : data && visibleGroups.length === 0 ? (
        <EmptyState title={search ? t`Keine passenden Produkte` : t`Noch keine Produkte`}
          action={search ? <button type="button" onClick={() => changeSearch('')}><Trans>Suche zurücksetzen</Trans></button>
            : canWrite && productMeta.can_create ? <button type="button" disabled={dialogBusy} onClick={() => void openForm(productMeta, null)}><Icon name="plus" /><Trans>Produkt anlegen</Trans></button> : undefined}>
          {search ? t`Versuchen Sie einen anderen Suchbegriff.` : t`Legen Sie ein Produkt an und ergänzen Sie anschließend seine Versionen.`}
        </EmptyState>
      ) : data ? <div className="component-table"><DataTable rows={visibleGroups} columns={columns} rowKey={(group) => group.key}
        renderDetail={renderDetail} expandedKeys={expanded} onToggleRow={(key) => {
          clearFocus(); setExpanded((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });
        }} pageSize={10} paginationResetKey={search} revealRowKey={focusKey} mobileBreakpoint={760}
        caption={t`Produkte und ihre Versionen`} rowClassName={(group) => group.key === focusKey ? 'stamm-focus' : undefined} /></div> : null}

      {dialog.kind === 'form' ? <CardFormModal meta={dialog.meta} row={dialog.row} initialValues={dialog.initialValues} notice={dialog.notice}
        readOnlyFields={dialog.initialValues ? ['ProduktTypeID'] : undefined}
        options={dialog.options} lookups={meta.lookups} onClose={() => setDialog({ kind: 'closed' })}
        onSubmit={async (values) => {
          const seq = dialogSeq.current;
          const saved = dialog.row ? await updateCmdbCard(dialog.meta.name, dialog.row.id, values) : await createCmdbCard(dialog.meta.name, values);
          if (seq !== dialogSeq.current) return;
          const classLabel = dialog.meta.label;
          const savedLabel = saved.label;
          await afterWrite(dialog.row ? t`${classLabel} „${savedLabel}“ gespeichert.` : t`${classLabel} „${savedLabel}“ angelegt.`);
          if (seq !== dialogSeq.current) return;
          setSearch(''); focusedRef.current = null;
          setParams({ klasse: dialog.meta.name, id: String(saved.id) });
        }} /> : null}
      {dialog.kind === 'delete' ? <DeleteCardModal meta={dialog.meta} row={dialog.row} blockers={dialog.row.referenced_by ?? []}
        onClose={() => setDialog({ kind: 'closed' })} onConfirm={async (cascade) => {
          const seq = dialogSeq.current;
          await deleteCmdbCard(dialog.meta.name, dialog.row.id, cascade);
          if (seq !== dialogSeq.current) return;
          const classLabel = dialog.meta.label;
          const deletedLabel = dialog.row.label;
          clearFocus(); await afterWrite(t`${classLabel} „${deletedLabel}“ gelöscht.`);
        }} /> : null}
    </section>
  );
}
